import React from "react";
import styled from "styled-components";

import { CustomerBreakoutColors } from "../../../../globalStyles";
import {
    IProfileRecord,
    ISegment,
    IStoreScoreRecord,
} from "../../../../Models/Models";
import {
    determinePropensityText,
    getPrettyNumber,
} from "../../../Common/SharedActions";

interface IRetailMatchPsychProfShareProps {
  brandName: string;
  locationAddress: string;
  sampleCount: number;
  showPropensityColumn: boolean;
  data: IProfileRecord[];
  segments: ISegment[];
  benchmarkRecord: IStoreScoreRecord;
  averageBrandHHCount: number;
  brandSegmentData: IProfileRecord[];
}

const Header = styled("div")`
  text-align: center;
  font-size: 15px;
  font-weight: bold;
  line-height: 20px;
  margin-bottom: 15px;
`;

const BreakoutContainer = styled("div")`
  border: 1px solid #e4e4e4;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 18px 21px;
`;

const BreakoutItemContainer = styled("div")`
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
`;

const TableHeaderContainer = styled("div")`
  display: flex;
  justify-content: space-evenly;
  margin-bottom: 3px;
`;

const TableContainer = styled("div")`
  display: grid;
  border: 1px solid #ededed;
`;

const TableCell = styled.div`
  display: flex;
  justify-content: space-between;
  height: 78px;
`;

const CircleContainer = styled("div")`
  margin: auto -22px auto 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;

const Circle = styled("div")`
  padding: 10px;
  font-weight: bold;
  margin-bottom: 3px;
  height: 48px;
  width: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Img = styled("img")`
  width: 58px;
  height: 58px;
  display: inline-block;
  border-radius: 50%;
  object-fit: cover;
  object-position: center;
  margin-right: 11px;
`;

const DataContainer = styled("div")`
  margin-left: -15px;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const BreakoutText = styled.div`
  display: flex;
  margin-bottom: 5px;
  font-size: 12px;
  font-weight: bolder;
`;

const BreakoutHighlight = styled.div`
  height: 12px;
  width: 12px;
  border-radius: 50%;
  margin-right: 4px;
  background-color: ${(props: { breakoutColor: string }) =>
        props.breakoutColor};
`;

const TradeAreaAddress = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

export const RetailMatchPsychProfShare = (
    props: IRetailMatchPsychProfShareProps
): JSX.Element => {
    const getSegment = (key: string): ISegment => {
        return props.segments.find((seg: ISegment) => {
            return seg.segmentKey === key;
        });
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "space-between",
            }}
        >
            <div>
                <Header>
                    {props.brandName}’s ideal customer segments based on lifestyle, life
          stage, behavioral, and consumer characteristics were identified based
          on mobile data observations of actual customers at the{" "}
                    {props.sampleCount.toLocaleString()} {props.brandName}’s locations.
                </Header>
                <BreakoutContainer>
                    <BreakoutItemContainer>
                        <BreakoutText>
                            <BreakoutHighlight breakoutColor={CustomerBreakoutColors.P} />
              Primary {props.brandName}’s Customer
                        </BreakoutText>
                        <div style={{ fontSize: 11 }}>
              Households 20+% more likely to visit {props.brandName} than the
              average household
                        </div>
                    </BreakoutItemContainer>
                    <BreakoutItemContainer>
                        <BreakoutText>
                            <BreakoutHighlight breakoutColor={CustomerBreakoutColors.S} />
              Secondary {props.brandName}’s Customer
                        </BreakoutText>
                        <div style={{ fontSize: 11 }}>
              Households up to 19% more likely to visit {props.brandName} than
              the average household
                        </div>
                    </BreakoutItemContainer>
                    <BreakoutItemContainer>
                        <BreakoutText>
                            <BreakoutHighlight breakoutColor={CustomerBreakoutColors.N} />
              Incidental {props.brandName}’s Customer
                        </BreakoutText>
                        <div style={{ fontSize: 11 }}>
              Households less likely to visit {props.brandName} than the average
              household
                        </div>
                    </BreakoutItemContainer>
                </BreakoutContainer>
            </div>
            <div>
                <TableHeaderContainer>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <div
                            style={{
                                fontWeight: "bold",
                                fontSize: 15,
                                marginRight: 25,
                            }}
                        >
              Top 5 Profiles
                        </div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                fontSize: 11,
                                textAlign: "center",
                            }}
                        >
                            <div
                                style={{
                                    textAlign: "center",
                                }}
                            >
                                <div>Trade Area of</div>
                                <TradeAreaAddress>
                                    <b>{props.locationAddress}</b>
                                </TradeAreaAddress>
                            </div>
                            <div
                                style={{
                                    marginLeft: 45,
                                }}
                            >
                COMPARED TO
                            </div>
                        </div>
                    </div>
                    <div
                        style={{
                            fontSize: 11,
                            display: "flex",
                            justifyContent: "space-between",
                            marginLeft: "20px",
                        }}
                    >
                        <div style={{ textAlign: "center" }}>
                            <div>Average Trade Area of</div>
                            <div>
                                <b>
                                    {props.sampleCount.toLocaleString()} {props.brandName}'s
                  Locations
                                </b>
                            </div>
                        </div>
                        {props.showPropensityColumn && (
                            <div style={{ marginLeft: 30 }}>
                                <div>Propensity to be an</div>
                                <div>
                                    <b>{props.brandName}'s Customer</b>
                                </div>
                            </div>
                        )}
                    </div>
                </TableHeaderContainer>
                <TableContainer>
                    {props.data.map((p: IProfileRecord, index: number) => {
                        const brandSeg: IProfileRecord = props.brandSegmentData.find(
                            (seg: IProfileRecord) => p.seg === seg.seg
                        );
                        const matchingSegment: ISegment = getSegment(p.seg);
                        return (
                            <div key={`rm-psyc-share-${p.seg}-${p.pct}`}>
                                <TableCell
                                    style={{
                                        padding: "0 82px 0 19px",
                                        borderTop: index % 2 > 0 && "none",
                                        borderBottom: index % 2 > 0 && "none",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Img src={`/Images/segmentation-imgs/${p.seg}.jpg`} />
                                        <div style={{ width: "137px" }}>
                                            <div style={{ fontSize: 12 }}>
                                                <b>
                                                    {matchingSegment.segmentKey} {matchingSegment.name}
                                                </b>
                                            </div>
                                            <div style={{ fontSize: 9 }}>
                                                {matchingSegment.description}
                                            </div>
                                        </div>
                                    </div>
                                    <DataContainer>
                                        <div>
                                            <b>
                                                {getPrettyNumber(
                                                    Math.ceil(props.benchmarkRecord.hhCnt * p.pct),
                                                    0
                                                )}
                                            </b>
                                        </div>
                                        <div>households</div>
                                    </DataContainer>

                                    <div
                                        style={{
                                            height: "100%",
                                            borderRight: "1px solid #e4e4e4",
                                        }}
                                    ></div>

                                    <DataContainer>
                                        <div>
                                            <b>
                                                {getPrettyNumber(
                                                    Math.ceil(props.averageBrandHHCount * brandSeg.pct),
                                                    0
                                                )}
                                            </b>
                                        </div>
                                        <div>households</div>
                                    </DataContainer>
                                    {props.showPropensityColumn && (
                                        <CircleContainer>
                                            <Circle
                                                style={{
                                                    border: `4px solid ${
                                                        CustomerBreakoutColors[brandSeg.core]
                                                    }`,
                                                }}
                                            >
                                                {getPrettyNumber(brandSeg.index, 1)}X
                                            </Circle>
                                            <div style={{ fontSize: 10 }}>
                                                <b>
                                                    {determinePropensityText(brandSeg.core).toUpperCase()}
                                                </b>
                                            </div>
                                        </CircleContainer>
                                    )}
                                </TableCell>
                            </div>
                        );
                    })}
                </TableContainer>
            </div>
        </div>
    );
};
